import type {
  ContentBlock,
  EpisodeState,
  PendingPrompt,
  PendingToolCall,
} from "../types/episode.ts";

/** Subset of EpisodeState required by the approval helpers. */
export type ApprovalReadState = Pick<EpisodeState, "toolCalls" | "messages" | "contentBlocks" | "toolResults">;

export const ASK_USER_MARKER_PREFIX = "[User selected from ask_user options]";
export const ASK_USER_MARKER_CLOSER = "[Proceed with the selected option — do NOT ask again]";

/**
 * Tool names that request a structured ask_user answer. Shared between the
 * queue selector, the decision summary, and any render path that needs to
 * classify a tool call.
 */
export const ASK_USER_TOOL_NAMES: ReadonlySet<string> = new Set([
  "ask_user",
  "episodes.ask_user",
  "episodes_ask_user",
  "episodes__ask_user",
]);

export function isAskUserName(name: string): boolean {
  return ASK_USER_TOOL_NAMES.has(name);
}

/**
 * Wrap free-form composer text as an ask_user answer that the agent will
 * recognize (same marker format as AskUserCard emits). Used by the web and
 * CLI composer-fallthrough paths so typed text is interchangeable with
 * card-submitted answers from the agent's perspective.
 */
export function wrapAskUserAnswer(text: string): string {
  return `${ASK_USER_MARKER_PREFIX}\n${text}\n${ASK_USER_MARKER_CLOSER}`;
}

function isInternalTool(name: string): boolean {
  return name.includes("set_title") || name.includes("append_message");
}

function classify(
  tc: PendingToolCall,
  hasRenderer: ((tc: PendingToolCall) => boolean) | undefined,
): PendingPrompt["kind"] | null {
  if (isAskUserName(tc.name)) return "ask_user";
  if (tc.approvalId !== undefined) return "approval";
  if (hasRenderer?.(tc) === true) return "module";
  return null;
}

/**
 * Build a FIFO pairing of ask_user `tool_call` IDs to their answering
 * `[User selected from ask_user options]` marker text, walking through the
 * persisted `contentBlocks` in order. Later ask_user prompts pair with
 * later marker messages so interleaved conversations resolve correctly.
 *
 * Returns a map keyed by ask_user toolCallId → marker-wrapped answer text.
 */
export function buildAskUserPairings(state: ApprovalReadState): Map<string, string> {
  const pairings = new Map<string, string>();
  const unanswered: string[] = [];
  const seen = new Set<string>();

  for (const block of state.contentBlocks as readonly ContentBlock[]) {
    if (block.type === "tool_call") {
      if (seen.has(block.toolCallId)) continue;
      const tc = state.toolCalls.find((t) => t.id === block.toolCallId);
      if (tc && isAskUserName(tc.name)) {
        seen.add(tc.id);
        unanswered.push(tc.id);
      }
    } else if (block.type === "user_message" && block.text.startsWith(ASK_USER_MARKER_PREFIX)) {
      const id = unanswered.shift();
      if (id !== undefined) {
        pairings.set(id, block.text);
      }
    }
  }
  return pairings;
}

/**
 * Can a prompt of this kind be dropped because a later same-name one exists?
 *
 * Supersede is a statement about DRAFTS: the agent re-proposed the same
 * action, so the earlier proposal is stale and hiding it costs nothing —
 * there is no state behind it.
 *
 * An approval-gated call is the opposite. It owns an `approval_id` and a
 * durable `pending_approval` row, and the backend's `has_open_waits` counts
 * it until something resolves it. Hiding it does not resolve it: the episode
 * stays parked in `needs_input` and the user can never reach the card that
 * would end the wait. Worse, the panel showed one card for twenty parked
 * approvals and no "N of M" counter, so each decision silently promoted an
 * identical-looking successor into the same slot — which is what "Deny does
 * not work" actually was.
 *
 * @tested-by: tst_fe_agentcore_008e
 */
function isSupersedable(kind: PendingPrompt["kind"]): boolean {
  return kind !== "approval";
}

export interface PendingPromptQueueOptions {
  readonly archived?: boolean;
  readonly submittedAskUser?: ReadonlySet<string>;
  /**
   * Predicate that tells the selector whether a pending tool call has a
   * frontend-side module renderer (e.g. ContactCreateRenderer,
   * EmailBatchSendRenderer). Those tools are actionable even without a
   * backend-emitted approvalId — the module renderer supplies its own
   * Approve/Deny/Edit UI. Agent-core stays renderer-agnostic: the frontend
   * passes `runtime.agent.resolveHistoryRenderer(...)` wrapped into a bool
   * and the CLI leaves this undefined.
   */
  readonly hasRenderer?: (toolCall: PendingToolCall) => boolean;
}

/**
 * Derive the queue of pending prompts that require user attention from the
 * full episode state. Ports the supersede + answered-ask_user inference
 * from AgentPanel.tsx so both the web PendingPromptPanel and the CLI share
 * the same filtering rules.
 *
 * - Skips non-pending and internal tool calls (set_title / append_message).
 * - Skips superseded calls — a pending call is superseded when a later
 *   pending call with the same tool name exists.
 * - Skips archived episodes entirely.
 * - Skips ask_user prompts already answered via either the optimistic
 *   `submittedAskUser` set or a per-id pairing against a persisted
 *   `user_message` block carrying the ask_user marker.
 * - Returns actionable approvals/module cards before ask_user prompts, while
 *   preserving original order inside each priority group.
 */
export function selectPendingPromptQueue(
  state: ApprovalReadState,
  opts: PendingPromptQueueOptions,
): PendingPrompt[] {
  if (opts.archived === true) return [];

  const submitted = opts.submittedAskUser ?? new Set<string>();
  const answered = buildAskUserPairings(state);

  const out: { readonly prompt: PendingPrompt; readonly index: number }[] = [];
  for (let i = 0; i < state.toolCalls.length; i += 1) {
    const tc = state.toolCalls[i]!;
    if (isInternalTool(tc.name)) continue;

    const kind = classify(tc, opts.hasRenderer);
    if (kind === null) continue;

    // Approval and module prompts only matter while the call is still
    // waiting on a result. Once `tc.status` flips out of "pending" the
    // user has approved/denied and DecisionSummary takes over.
    //
    // ask_user is special: backend's `episodes.ask_user` returns
    // `{status:"question_sent"}` synchronously, so `tc.status` is
    // already "complete" by the time the queue is computed. Its real
    // lifecycle is tracked via `submitted` + `answered` below.
    if (kind !== "ask_user" && tc.status !== "pending") continue;

    // Supersede: a later same-name call kicks the older one — but NEVER an
    // approval. See `isSupersedable`.
    const superseded =
      isSupersedable(kind) &&
      state.toolCalls.some(
        (other, j) =>
          j > i &&
          other.name === tc.name &&
          (kind === "ask_user" || other.status === "pending"),
      );
    if (superseded) continue;

    if (kind === "ask_user") {
      if (submitted.has(tc.id)) continue;
      if (answered.has(tc.id)) continue;
    }

    out.push({ prompt: { toolCall: tc, kind }, index: i });
  }
  return out
    .sort((left, right) => {
      const priority = (kind: PendingPrompt["kind"]): number => {
        if (kind === "approval") return 0;
        if (kind === "module") return 1;
        return 2;
      };
      const diff = priority(left.prompt.kind) - priority(right.prompt.kind);
      return diff !== 0 ? diff : left.index - right.index;
    })
    .map((entry) => entry.prompt);
}

/**
 * Compute the set of tool_call IDs that are pending AND superseded
 * (another later pending call with the same name exists). Used by the feed
 * renderer to collapse stale prompts into a compact superseded placeholder.
 */
export function selectSupersededToolCallIds(state: ApprovalReadState): Set<string> {
  const ids = new Set<string>();
  for (let i = 0; i < state.toolCalls.length; i += 1) {
    const tc = state.toolCalls[i]!;
    if (tc.status !== "pending") continue;
    // An approval is never superseded — it owns a durable backend wait that
    // only a decision can close. Collapsing it here would also disagree with
    // `selectPendingPromptQueue`, which still offers it. @tested-by:
    // tst_fe_agentcore_008e
    if (tc.approvalId !== undefined) continue;
    const hasLaterSameName = state.toolCalls.some(
      (other, j) => j > i && other.name === tc.name && other.status === "pending",
    );
    if (hasLaterSameName) ids.add(tc.id);
  }
  return ids;
}
